import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import './device-tabs.css'
import { useState, type FormEvent } from 'react'
import {
  deviceDetectionsQuery,
  deviceEdrEventsQuery,
  deviceEventsQuery,
  deviceFilesQuery,
  deviceSettingsQuery,
  deviceSummaryQuery,
  deviceTabsQuery,
  EMPTY_EDR_EVENT_FILTERS,
  saveDeviceSettings,
  type EdrEventFilterValues,
  type Period,
  type SettingField,
} from '../api/securitee'
import { EdrEventsFilters } from './EdrEventsFilters'
import { ApiError } from '../api/client'
import { escapeHtml } from '../inventoree/format'
import { DeviceAgentOperations } from '../devices/DeviceAgentOperations'
import { DeviceGravityZoneTab } from './gravityzone/DeviceGravityZoneTab'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { NestedTable, type NestedColumn } from './NestedTable'
import { SettingsRadio } from '../ui/SettingsParts'
import { RansomwareResponse } from './RansomwareResponse'
import { CfaDeviceSetting } from './CfaDeviceSetting'
import { DeviceBaseline } from './DeviceBaseline'
import { mitreHtml, severityHtml } from './columns'

/**
 * Randul desfacut al unei masini din Securitee - `Securitee::getActionTabs`
 * (devices/others/actionTabs.php) cu filele lui: Summary, Transferred Files,
 * Antiransomware, Settings, Events, si "Security scan" din caseta Diagnostic.
 * Cele trei file de Antivirus (doar UserID 5913) nu sunt aici: prototipul
 * Bitdefender e DROPPED.
 *
 * SECURITY SCAN. Pana pe 24 septembrie 2026 era un `<a href>` catre
 * `diagnostic_reports/device_scan/<id>/security_scan`, trecut prin `navUrl()`.
 * Adresa aia nu e in `MOVED` si n-are ruta in `router.tsx`, deci `navUrl` cadea
 * pe `portalUrl()` si legatura IESEA din /app, spre portalul CodeIgniter care
 * nu mai ruleaza pe `optimumdesk.ai`: un buton mort.
 *
 * Acum e a sasea fila a randului, si deschide PE LOC acelasi panou de scanare
 * pe care il are fila Operations a fisei (`DeviceAgentOperations`), numai cu
 * alta cheie de raport - `security_scan`, comanda SSD. Butonul se deseneaza
 * numai cand capatul a spus cheia, adica numai cand omul are
 * `securitee.security_scan.read`.
 */
/** `tab`: fila ceruta din adresa (pagina aparatului, `?tab=`); `onTab` afla de fila aleasa. */
export function SecuriteeDeviceTabs({ id, period, tab: wanted, onTab }: { id: number; period: Period; tab?: string; onTab?(key: string): void }) {
  const tabs = useQuery(deviceTabsQuery(id))
  const [chosen, setTab] = useState(wanted ?? 'summary')
  const pick = (key: string) => {
    setTab(key)
    onTab?.(key)
  }
  if (tabs.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {tabs.error.message}
      </div>
    )
  }
  if (!tabs.data) return <Spinner />
  // o fila pe care serverul n-a dat-o (fara drept, fara EDR) nu se deschide din adresa
  const offered = tabs.data.tabs.some((t) => t.key === chosen) || (chosen === 'security_scan' && tabs.data.security_scan !== '')
  const tab = offered ? chosen : 'summary'

  return (
    <div className="row d-flex m-0 xdr-device-tabs">
      <div className="col-auto bg-lightest-gray xdr-device-tabs-menu">
        <ul className="action_tabs">
          {tabs.data.tabs.map((t) => (
            <li key={t.key}>
              <a
                href="#"
                className={`action_button${tab === t.key ? ' active' : ''}`}
                onClick={(e) => {
                  e.preventDefault()
                  pick(t.key)
                }}
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
        {tabs.data.security_scan === '' ? null : (
          <div className="quick-view-modules">
            <p style={{ fontSize: 14, padding: '4px 6px', color: '#000' }}>
              <i className="fas fa-cogs" aria-hidden="true"></i> Diagnostic
            </p>
            <ul className="action_tabs">
              <li>
                <a
                  href="#"
                  className={`action_button color-red${tab === 'security_scan' ? ' active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    pick('security_scan')
                  }}
                >
                  Security scan
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
      <div className="col-md-12 xdr-device-tabs-content">
        <div className="details_content">
          {tab === 'summary' && <SummaryTab id={id} period={period} />}
          {tab === 'files' && <FilesTab id={id} period={period} />}
          {tab === 'antiransomware' && (
            <>
              <SettingsTab id={id} kind="antiransomware" />
              <CfaDeviceSetting id={id} />
              <RansomwareResponse id={id} />
            </>
          )}
          {tab === 'settings' && <SettingsTab id={id} kind="settings" />}
          {tab === 'events' && <EventsTab id={id} />}
          {tab === 'detections' && <DetectionsTab id={id} />}
          {tab === 'edr_events' && <EdrEventsTab id={id} period={period} />}
          {tab === 'gravityzone' && <DeviceGravityZoneTab id={id} />}
          {tab === 'security_scan' && <DeviceAgentOperations id={id} report={tabs.data.security_scan} />}
        </div>
      </div>
    </div>
  )
}

function Stat({ color, icon, number, label, cols }: { color: string; icon: string; number: string | number; label: string; cols: number }) {
  return (
    <div className={`col-md-${cols}`}>
      <div className={`dashboard-stat ${color}`}>
        <div className="visual">
          <i className={icon}></i>
        </div>
        <div className="details">
          <div className="number">{number}</div>
          <div className="desc"></div>
        </div>
        <div className="more">{label}</div>
      </div>
    </div>
  )
}

/** devices/others/summary.php */
function SummaryTab({ id, period }: { id: number; period: Period }) {
  const q = useQuery(deviceSummaryQuery(id, period))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const s = q.data
  return (
    <>
      <div className="row">
        <Stat color="red-intense" icon="fa fa-unlock-alt" number={s.antivirusStatus} label="Antivirus Status" cols={3} />
        <Stat color="blue-madison" icon="fa fa-folder" number={s.files} label="Total Files" cols={2} />
        <Stat color="blue-madison" icon="far fa-save" number={s.files_size} label="Total Size" cols={2} />
        <Stat color="blue-madison" icon="fa fa-file" number={s.documents} label="Documents" cols={2} />
        <Stat color="blue-madison" icon="far fa-save" number={s.documents_size} label="Documents Size" cols={2} />
      </div>
      <DeviceBaseline baseline={s.baseline ?? null} />
      <div className="row">
        <div className="col-md-12 divider">
          <h4 className="bordered">Users Logged Summary</h4>
        </div>
        <div className="col-md-12 ">
          <table className="table table-striped centered">
            <thead>
              <tr>
                <th>User Logged On</th>
                <th>Total Files</th>
                <th>Total Size</th>
              </tr>
            </thead>
            <tbody>
              {s.loggedUserSummary.length === 0 ? (
                <tr>
                  <td><span style={{ marginLeft: 10 }}>N/A</span></td>
                  <td><span style={{ marginLeft: 10 }}>N/A</span></td>
                  <td><span style={{ marginLeft: 10 }}>N/A</span></td>
                </tr>
              ) : (
                s.loggedUserSummary.map((u) => (
                  <tr key={u.LoggedUser}>
                    <td><span style={{ marginLeft: 10 }}>{u.LoggedUser}</span></td>
                    <td><span style={{ marginLeft: 10 }}>{u.Files}</span></td>
                    <td><span style={{ marginLeft: 10 }}>{u.Size}</span></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

const FILE_COLUMNS: NestedColumn[] = [
  { key: 'username', title: 'User Logged On', html: (r) => escapeHtml(r.username) },
  { key: 'file_name', title: 'File name', html: (r) => `<span style="word-wrap:break-word; display:block; width:150px;">${escapeHtml(r.file_name)}</span>` },
  { key: 'file_destination', title: 'File Destination', html: (r) => `<span style="word-wrap:break-word; display:block; width:150px;">${escapeHtml(r.file_destination)}</span>` },
  { key: 'file_size', title: 'Size', html: (r) => escapeHtml(r.file_size) },
  { key: 'file_source', title: 'Destination Source', html: (r) => escapeHtml(r.file_source) },
  { key: 'device_date', title: 'Device Date', html: (r) => escapeHtml(r.device_date) },
  { key: 'server_date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => escapeHtml(r.server_date) },
]

/** devices/others/files.php: filtrele Files Type / Source Type + tabelul #deviceFiles. */
function FilesTab({ id, period }: { id: number; period: Period }) {
  const [fileType, setFileType] = useState(0)
  const [fileSource, setFileSource] = useState(0)
  const [state, setState] = useState({ start: 0, length: 10, order: { column: 6, dir: 'desc' as 'asc' | 'desc' }, search: '' })
  const col = FILE_COLUMNS[state.order.column] ?? FILE_COLUMNS[6]!
  const q = useQuery({
    ...deviceFilesQuery(id, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search, from: period.from, to: period.to, fileType, fileSource }),
    placeholderData: keepPreviousData,
  })
  return (
    <div className="row">
      <div className="col-md-12 btn-group">
        <div className="btn-group pull-right" style={{ marginLeft: 10 }}>
          <select className="btn btn-primary btn" value={fileSource} onChange={(e) => { setFileSource(Number(e.target.value)); setState({ ...state, start: 0 }) }} aria-label="Source Type">
            <option value={0}>Source Type: All</option>
            <option value={1}>Source Type: Email</option>
            <option value={2}>Source Type: USB</option>
          </select>
        </div>
        <div className="btn-group pull-right">
          <select className="btn btn-primary btn" value={fileType} onChange={(e) => { setFileType(Number(e.target.value)); setState({ ...state, start: 0 }) }} aria-label="Files Type">
            <option value={0}>Files Type: All Files</option>
            <option value={1}>Files Type: Documents</option>
          </select>
        </div>
      </div>
      <div className="col-md-12">
        <NestedTable id="deviceFiles" columns={FILE_COLUMNS} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
      </div>
    </div>
  )
}

const LOG_COLUMNS: NestedColumn[] = [
  { key: 'event_date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => escapeHtml(r.event_date) },
  { key: 'event_message', title: 'Message', html: (r) => escapeHtml(r.event_message) },
  { key: 'user_id', title: 'User', html: (r) => (r.Prenume ? escapeHtml(`${r.Prenume} ${r.Nume ?? ''}`) : '<strong>System</strong>') },
]

/** devices/others/events.php - jurnalul it_securitee_logs al masinii. */
function EventsTab({ id }: { id: number }) {
  const [state, setState] = useState({ start: 0, length: 10, order: { column: 0, dir: 'desc' as 'asc' | 'desc' }, search: '' })
  const col = LOG_COLUMNS[state.order.column] ?? LOG_COLUMNS[0]!
  const q = useQuery({
    ...deviceEventsQuery(id, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search }),
    placeholderData: keepPreviousData,
  })
  return (
    <div className="row">
      <div className="col-md-12">
        <NestedTable id="deviceEvents" columns={LOG_COLUMNS} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
      </div>
    </div>
  )
}

/** Fila Detections (XDR): alertele Wazuh ale masinii; ordinea = `Detections::COLUMNS` fara aparat. */
const DETECTION_COLUMNS: NestedColumn[] = [
  { key: 'detected_at', title: 'Date (UTC)', html: (r) => escapeHtml(r.detected_at) },
  { key: 'rule_level', title: 'Severity', html: (r) => severityHtml(r.severity) },
  { key: 'title', title: 'Detection', html: (r) => escapeHtml(r.title) },
  { key: 'mitre', title: 'MITRE ATT&CK', html: mitreHtml },
  { key: 'rule_id', title: 'Rule', html: (r) => `${escapeHtml(r.rule_id)} (level ${Number(r.rule_level)})` },
]

function DetectionsTab({ id }: { id: number }) {
  const [state, setState] = useState({ start: 0, length: 10, order: { column: 0, dir: 'desc' as 'asc' | 'desc' }, search: '' })
  const col = DETECTION_COLUMNS[state.order.column] ?? DETECTION_COLUMNS[0]!
  const q = useQuery({
    ...deviceDetectionsQuery(id, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search }),
    placeholderData: keepPreviousData,
  })
  return (
    <div className="row">
      <div className="col-md-12">
        <NestedTable id="deviceDetections" columns={DETECTION_COLUMNS} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
      </div>
    </div>
  )
}

/** Fila EDR events (XDR): telemetria bruta a masinii (`it_edr_events`), descrisa pe o linie. */
const EDR_EVENT_COLUMNS: NestedColumn[] = [
  { key: 'event_time', title: 'Time (UTC)', html: (r) => escapeHtml(r.event_time) },
  { key: 'channel', title: 'Channel', html: (r) => escapeHtml(r.channel) },
  { key: 'event_id', title: 'Event ID', html: (r) => escapeHtml(r.event_id) },
  { key: 'text', title: 'Event', html: (r) => escapeHtml(r.text) },
]

function EdrEventsTab({ id, period }: { id: number; period: Period }) {
  const [filters, setFilters] = useState<EdrEventFilterValues>(EMPTY_EDR_EVENT_FILTERS)
  const [state, setState] = useState({ start: 0, length: 10, order: { column: 0, dir: 'desc' as 'asc' | 'desc' }, search: '' })
  const col = EDR_EVENT_COLUMNS[state.order.column] ?? EDR_EVENT_COLUMNS[0]!
  const q = useQuery({
    ...deviceEdrEventsQuery(id, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search, ...period }, filters),
    placeholderData: keepPreviousData,
  })
  const facets = q.data?.facets ?? { channels: [], eventIds: [] }

  return (
    <div className="row">
      <div className="col-md-12">
        <EdrEventsFilters filters={filters} facets={facets} onChange={(next) => { setFilters(next); setState((s) => ({ ...s, start: 0 })) }} />
        <NestedTable id="deviceEdrEvents" columns={EDR_EVENT_COLUMNS} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
      </div>
    </div>
  )
}

/**
 * `extra` e a treia treapta a randului, intr-o coloana a ei: Read-only la USB
 * (1), Block executables la Email Scanning (2). `blockText` spune, sub numele
 * randului, ce face treapta Block pe masina - fiecare buton face ceva cinstit, si
 * textul spune exact ce, si ce nu.
 */
export type ExtraOption = { value: number; label: string }
type Option = { field: SettingField; label: string; block: number; extra?: ExtraOption; blockText?: string; defaultBlock?: boolean; limits?: string }

/** Limitele USB, cum le scrie agentul (usb_windows.go). */
export const USB_LIMITS =
  'Applies from the next time a device is connected; a device already connected stays usable until it is removed. A domain Group Policy on the same settings wins, and a local administrator can undo it.'

/** Ce face Web Uploads = Block si ce nu (agentul: web_windows.go, AllowFileSelectionDialogs). */
export const WEB_UPLOADS_LIMITS =
  'Blocks the file picker in Edge and Chrome. Drag and drop, paste and other browsers are not blocked.'

/**
 * Ce face treapta Block executables a lui Email Scanning (agentul: mail_windows.go,
 * regula ASR Defender, aprinsa pe `email_executables` = 1).
 */
export const EMAIL_SCANNING_LIMITS =
  'Block executables: blocks executable files and scripts that arrive by email (Microsoft Defender rule; needs Microsoft Defender as the active antivirus).'

/**
 * Ce face Block pe Email Scanning (documentele trimise pe mail): agentul primeste
 * `email_block_documents` = 1 si pune aceeasi politica de browser ca Web Uploads
 * (web_windows.go, AllowFileSelectionDialogs). Outlook desktop nu are politica
 * pentru atasarea la mesajele trimise, deci nu e acoperit - si se spune.
 */
export const EMAIL_BLOCK_DOCUMENTS_TEXT =
  'Block: turns off the file picker in Edge and Chrome, so files cannot be attached in webmail. Drag and drop, paste, other browsers and desktop mail apps such as Outlook are not blocked.'

/**
 * Ce face Block pe Extension Protection (`extension_protection` = 1): detectie pe
 * statii (windows-agent blocker.go, eveniment tip 4) si FSRM pe servere
 * (winserver-agent fsrm_windows.go).
 */
export const EXTENSION_PROTECTION_TEXT =
  "Reports files with the company's ransomware extensions on workstations and blocks creating them on the shares of Windows servers with File Server Resource Manager."

/** A treia treapta a lui USB. */
export const USB_READ_ONLY: ExtraOption = { value: 1, label: 'Read-only' }
/** A treia treapta a lui Email Scanning: regula ASR de executabile (coloana EmailScanning = 2). */
export const EMAIL_BLOCK_EXECUTABLES: ExtraOption = { value: 2, label: 'Block executables' }

/**
 * Unde se impune Files Creation Blocker: numai agentul de SERVERE, prin File
 * Server Resource Manager, pe partajarile de disc
 * (winserver-agent/internal/securitee/fsrm_windows.go). Pe statii agentul doar
 * DETECTEAZA si raporteaza fisierele (windows-agent/internal/securitee/blocker.go).
 */
export const FILES_BLOCKER_LIMITS =
  'Blocks creating files that match the company list on the shares of Windows servers with File Server Resource Manager. On workstations it reports such files but does not block them.'

/** Ce face Registry Immunizer, cum il face agentul (registry_windows.go): urmareste, nu blocheaza. */
export const REGISTRY_IMMUNIZER_TEXT =
  'Watches the registry keys in the company list and reports every change; keys set to Revert are put back. It does not stop the change from being made.'

/** Textul treptei Block, sub numele randului, in stilul lui securitee-usb-limits. */
export function BlockText({ text }: { text: string }) {
  return (
    <div className="securitee-usb-limits securitee-block-text" style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400, marginTop: 4 }}>
      {text}
    </div>
  )
}


/** devices/others/settings.php si antiransomware.php - aceleasi randuri Option / Active / Block. */
const OPTIONS: Record<'settings' | 'antiransomware', Option[]> = {
  settings: [
    { field: 'Usb', label: 'USB', block: 2, extra: USB_READ_ONLY, limits: USB_LIMITS },
    { field: 'EmailScanning', label: 'Email Scanning', block: 1, extra: EMAIL_BLOCK_EXECUTABLES, blockText: EMAIL_BLOCK_DOCUMENTS_TEXT, limits: EMAIL_SCANNING_LIMITS },
    { field: 'WebUploads', label: 'Web Uploads', block: 1, limits: WEB_UPLOADS_LIMITS },
    { field: 'PublicShares', label: 'Public Shares', block: 1 },
    { field: 'AdminShares', label: 'Admin Shares', block: 1 },
  ],
  antiransomware: [
    { field: 'CryptoLocker', label: 'Extension Protection', block: 1, defaultBlock: true, blockText: EXTENSION_PROTECTION_TEXT },
    { field: 'RegistryImmunizer', label: 'Registry Immunizer', block: 1, defaultBlock: true },
    { field: 'FilesCreationBlocker', label: 'Files Creation Blocker', block: 1, defaultBlock: true },
  ],
}

function SettingsTab({ id, kind }: { id: number; kind: 'settings' | 'antiransomware' }) {
  const client = useQueryClient()
  const q = useQuery(deviceSettingsQuery(id))
  const [busy, setBusy] = useState(false)
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const current = q.data.settings
  const options = OPTIONS[kind]

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (busy) return
    const data = new FormData(e.currentTarget)
    const settings: Partial<Record<SettingField, number>> = {}
    for (const o of options) {
      const v = data.get(`settings[${o.field}]`)
      if (v !== null && v !== '') settings[o.field] = Number(v)
    }
    setBusy(true)
    try {
      const body = await saveDeviceSettings(id, settings)
      toast(body.message, 'Success', 'success')
      void client.invalidateQueries({ queryKey: ['securitee', 'device', id] })
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Something went wrong.', 'Error', 'error')
    } finally {
      setBusy(false)
    }
  }

  const cell = { textAlign: 'center' as const, background: '#b3b3b3' }
  return (
    <form method="post" action={`/securitee/saveDeviceSettings`} id="device_settings" onSubmit={onSubmit}>
      <input type="hidden" name="deviceId" value={id} />
      <table className="table table-hover">
        <thead>
          <tr>
            <th className="a-left" style={{ width: 200 }}>Option</th>
            <th className="a-left" style={{ width: 200, textAlign: 'center' }}>Active</th>
            <th className="a-left" style={{ width: 200, textAlign: 'center' }}>Block</th>
            <th className="a-left" style={{ textAlign: 'center' }}></th>
          </tr>
        </thead>
        <tbody>
          {options.map((o) => {
            const value = current[o.field]
            /* Fara rand in baza, antiransomware porneste pe Block, ca in macheta. */
            const chosen = value === null ? (o.defaultBlock ? o.block : null) : value
            return (
              <tr key={o.field}>
                <td className="a-left">
                  <strong>{o.label}</strong>
                  {o.blockText ? <BlockText text={o.blockText} /> : null}
                  {o.limits ? <div className="securitee-usb-limits" style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400, marginTop: 4 }}>{o.limits}</div> : null}
                  {o.field === 'FilesCreationBlocker' ? <div className="securitee-usb-limits securitee-files-blocker-limits" style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400, marginTop: 4 }}>{FILES_BLOCKER_LIMITS}</div> : null}
                  {o.field === 'RegistryImmunizer' ? <div className="securitee-usb-limits" style={{ fontSize: 11, color: '#7f8c8d', fontWeight: 400, marginTop: 4 }}>{REGISTRY_IMMUNIZER_TEXT}</div> : null}
                </td>
                <td style={cell} className="a-left">
                  <SettingsRadio aria-label={`${o.label}: active`} className="radio_blue" name={`settings[${o.field}]`} value={0} defaultChecked={chosen === 0} />
                </td>
                <td style={cell} className="a-left">
                  <SettingsRadio aria-label={`${o.label}: block`} className="radio_red" name={`settings[${o.field}]`} value={o.block} defaultChecked={chosen === o.block} />
                </td>
                <td style={{ textAlign: 'center' }} className="a-left">
                  {o.extra ? (
                    <div style={{ margin: '0 auto', width: 150 }}>
                      <SettingsRadio className="securitee_settings" name={`settings[${o.field}]`} value={o.extra.value} defaultChecked={chosen === o.extra.value} label={o.extra.label} />
                    </div>
                  ) : null}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <input type="submit" className="btn btn-primary save_device_settings" value="Save" disabled={busy} />
    </form>
  )
}
